import { Suspense, lazy, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { convexQuery } from '@convex-dev/react-query'
import { Box, Grid, Paper, Skeleton, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useMutation, useQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { useProfile } from '@/hooks/convex/useProfile'
import { sortTasksByDefault } from '@/features/tasks/utils/sortTasksByDefault'
import { useProjects } from '@/features/projects/hooks/useProjects'
import { IntelligentSummary } from '@/features/dashboard/components/IntelligentSummary'
import { HighPriorityTaskCard } from '@/features/dashboard/components/HighPriorityTaskCard'
import { QuickFilterTasks } from '@/features/dashboard/components/QuickFilterTasks'
import { EmptyDashboardPrompt } from '@/features/dashboard/components/EmptyDashboardPrompt'
import { DiscoveryRow } from '@/features/dashboard/components/DiscoveryRow'
import classes from '@/features/dashboard/components/Dashboard.module.css'

// Lazy load below-fold sections for better initial load performance
const ProjectsSection = lazy(() =>
  import('@/features/projects/components/ProjectsSection').then(m => ({ default: m.ProjectsSection }))
)
const QueueSummaryCard = lazy(() =>
  import('@/features/queue/components/QueueSummaryCard').then(m => ({ default: m.QueueSummaryCard }))
)
const AssetSummarySection = lazy(() =>
  import('@/features/assets/components/AssetSummarySection').then(m => ({ default: m.AssetSummarySection }))
)
const RewardsSummaryWidget = lazy(() =>
  import('@/features/rewards/components/RewardsSummaryWidget').then(m => ({ default: m.RewardsSummaryWidget }))
)

export const Route = createFileRoute('/_authed/dashboard')({
  loader: async ({ context }) => {
    // Prefetch tasks for above-fold components (HighPriorityTaskCard, QuickFilterTasks)
    // Profile is already prefetched and validated by parent _authed route
    // Unified schema: uses api.tasks.getDueTasks (replaces api.recurring.getDueTasks)
    await Promise.all([
      context.queryClient.ensureQueryData(
        convexQuery(api.tasks.getDueTasks, { daysAhead: 30 })
      ),
      context.queryClient.ensureQueryData(
        convexQuery(api.rewards.getNextRewardGoal, {})
      ),
    ])
  },
  component: DashboardRoute,
})

// Skeleton components for lazy-loaded sections
function ProjectsSkeleton() {
  return (
    <Box>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Projects</Text>
      <Stack gap="xs">
        <Skeleton height={80} radius="md" />
        <Skeleton height={80} radius="md" />
      </Stack>
    </Box>
  )
}

function QueueSkeleton() {
  return (
    <Box>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Setup Queue</Text>
      <Paper p="sm" radius="md" withBorder>
        <Skeleton height={60} radius="md" />
      </Paper>
    </Box>
  )
}

function RewardsSkeleton() {
  return (
    <Paper p="xs" radius="md" withBorder>
      <Skeleton height={24} radius="md" />
    </Paper>
  )
}

function AssetsSkeleton() {
  return (
    <Box>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Assets</Text>
      <Stack gap="xs">
        <Skeleton height={60} radius="md" />
        <Skeleton height={60} radius="md" />
      </Stack>
    </Box>
  )
}

/**
 * Generate an intelligent summary based on user's data
 */
function generateSummary(
  tasks: Array<{ dueDate: number; status: string; priority: number }>,
): string {
  const now = Date.now()
  const pendingTasks = tasks.filter(
    (t) => t.status !== 'completed' && t.status !== 'cancelled'
  )
  const overdueTasks = pendingTasks.filter((t) => t.dueDate < now)
  const highPriorityTasks = pendingTasks.filter((t) => t.priority >= 3)
  const todayTasks = pendingTasks.filter((t) => {
    const taskDate = new Date(t.dueDate)
    const today = new Date()
    return (
      taskDate.getDate() === today.getDate() &&
      taskDate.getMonth() === today.getMonth() &&
      taskDate.getFullYear() === today.getFullYear()
    )
  })

  if (overdueTasks.length > 0) {
    return `You have ${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''} that need attention. Let's tackle them first!`
  }

  if (todayTasks.length > 0) {
    return `You have ${todayTasks.length} task${todayTasks.length > 1 ? 's' : ''} due today. ${highPriorityTasks.length > 0 ? `${highPriorityTasks.length} ${highPriorityTasks.length > 1 ? 'are' : 'is'} high priority.` : 'You\'ve got this!'}`
  }

  if (pendingTasks.length === 0) {
    return "All caught up! No pending tasks. Enjoy your day or add some new tasks."
  }

  return `You have ${pendingTasks.length} upcoming task${pendingTasks.length > 1 ? 's' : ''}. ${highPriorityTasks.length > 0 ? `Focus on the ${highPriorityTasks.length} high priority ${highPriorityTasks.length > 1 ? 'ones' : 'one'} first.` : 'Take your time and stay focused.'}`
}

/**
 * Get the greeting based on time of day
 */
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good Morning'
  if (hour < 17) return 'Good Afternoon'
  return 'Good Evening'
}

function DashboardRoute() {
  const { profile } = useProfile()

  // Use tasks data - already prefetched in loader
  // Unified schema: uses api.tasks.getDueTasks (replaces api.recurring.getDueTasks)
  const tasks = useQuery(api.tasks.getDueTasks, { daysAhead: 30 })
  const { projects } = useProjects()
  const completeTaskMutation = useMutation(api.tasks.completeTask)

  // Pending tasks in intelligent default order
  // Map Convex tasks (_id) to sortable format (id) for sortTasksByDefault
  const pendingTasksSorted = useMemo(() => {
    const allTasks = tasks ?? []
    const pending = allTasks
      .filter((t: any) => t.status !== 'completed' && t.status !== 'cancelled')
      .map((t: any) => ({
        ...t,
        id: t._id, // sortTasksByDefault expects 'id' not '_id'
      }))
    return sortTasksByDefault(pending)
  }, [tasks])

  const handleComplete = async (taskId: string, _type: 'task' | 'recurring') => {
    try {
      // Unified schema: all tasks use completeTask
      const result = await completeTaskMutation({ taskId: taskId as Id<'tasks'> })

      if (result.isRecurring && result.nextDueDate) {
        const nextDueFormatted = new Date(result.nextDueDate).toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
        notifications.show({
          title: 'Task Completed!',
          message: `Great work! Next occurrence: ${nextDueFormatted}${result.rewardValue ? ` (+${result.rewardValue} reward)` : ''}`,
          color: 'green',
          icon: <IconCheck size={20} />,
          autoClose: 4000,
        })
      } else {
        notifications.show({
          title: 'Task Completed',
          message: result.rewardValue
            ? `Task has been marked as complete (+${result.rewardValue} reward)`
            : 'Task has been marked as complete',
          color: 'green',
          icon: <IconCheck size={20} />,
        })
      }
    } catch (_error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to complete task',
        color: 'red',
        icon: <IconX size={20} />,
      })
    }
  }

  // Layout blocks until profile exists
  if (!profile) {
    return null
  }

  // Show skeleton while tasks are loading (should be fast since prefetched)
  if (!tasks) {
    return (
      <Box className={classes.dashboardContainer}>
        <Skeleton height={80} radius="md" mb="md" />
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Stack gap="md">
              <Skeleton height={120} radius="md" />
              <Skeleton height={200} radius="md" />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Stack gap="md">
              <Skeleton height={100} radius="md" />
              <Skeleton height={150} radius="md" />
            </Stack>
          </Grid.Col>
        </Grid>
      </Box>
    )
  }

  const highestPriorityTask = pendingTasksSorted[0] ?? null
  const userName = profile.firstName || 'there'
  const greeting = getGreeting()
  const summaryText = generateSummary(tasks)

  // Show empty state prompt for new users with no projects and no tasks
  const isNewUser = projects.length === 0 && pendingTasksSorted.length === 0
  if (isNewUser) {
    return <EmptyDashboardPrompt userName={userName} />
  }

  // Project-first layout: for users who came via DIY projects and have few tasks.
  // Promotes projects to the top of the left column so blog-acquired users
  // immediately see their saved work rather than an empty task list.
  const showProjectsFirst = projects.length > 0 && pendingTasksSorted.length < 3

  const remainingTasks = pendingTasksSorted.filter(
    (t) => highestPriorityTask ? t.id !== highestPriorityTask.id : true
  )

  return (
    <Box className={classes.dashboardContainer}>
      {/* Row 1: Summary - above fold, renders immediately */}
      <IntelligentSummary
        userName={userName}
        greeting={greeting}
        summaryText={summaryText}
      />

      {/* Two columns layout */}
      <Grid className={classes.dashboardGrid} gutter="sm" mt="md">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack className={classes.mobileStack} gap="md">
            {showProjectsFirst ? (
              <>
                {/* Projects first for blog-acquired users */}
                <Suspense fallback={<ProjectsSkeleton />}>
                  <ProjectsSection />
                </Suspense>
                {highestPriorityTask && (
                  <HighPriorityTaskCard
                    task={highestPriorityTask}
                    onComplete={handleComplete}
                  />
                )}
                {remainingTasks.length > 0 && (
                  <QuickFilterTasks
                    tasks={remainingTasks}
                    onComplete={handleComplete}
                  />
                )}
              </>
            ) : (
              <>
                {/* Tasks first for established users */}
                <HighPriorityTaskCard
                  task={highestPriorityTask}
                  onComplete={handleComplete}
                />
                <QuickFilterTasks
                  tasks={remainingTasks}
                  onComplete={handleComplete}
                />
                <Suspense fallback={<ProjectsSkeleton />}>
                  <ProjectsSection />
                </Suspense>
              </>
            )}
          </Stack>
        </Grid.Col>

        {/* Right column: discovery (for project users), rewards, queue, assets */}
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack className={classes.mobileStack} gap="md">
            {showProjectsFirst && <DiscoveryRow />}
            <Box className={classes.rewardsWrapper}>
              <Suspense fallback={<RewardsSkeleton />}>
                <RewardsSummaryWidget />
              </Suspense>
            </Box>
            <Suspense fallback={<QueueSkeleton />}>
              <QueueSummaryCard />
            </Suspense>
            <Suspense fallback={<AssetsSkeleton />}>
              <AssetSummarySection />
            </Suspense>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  )
}
